import React, { useState, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { apiAdmin } from '../services/apiService';
import { toast } from '../utils/toast';
import { MasterDataImportExport } from '../components/admin/MasterDataImportExport';

interface APITransformationResult {
  total_apis: number;
  transformed_apis: number;
  transformations: Array<{
    api_id: number;
    api_name: string;
    changes: Array<{
      field: string;
      old_value: any;
      new_value: any;
    }>;
  }>;
  errors: string[];
}

interface ValidationResult {
  total_apis: number;
  valid_apis: number;
  invalid_apis: number;
  validation_issues: Array<{
    api_id: number;
    api_name: string;
    issues: string[];
  }>;
}

interface MigrationResult {
  total_apis: number;
  successful: number;
  failed: number;
  updated?: number;
  errors: string[];
}

export const AdminApiContentTransformerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'transform' | 'validate' | 'export' | 'import' | 'bulk-update'>('transform');
  const [isLoading, setIsLoading] = useState(false);
  const [transformResult, setTransformResult] = useState<APITransformationResult | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bulkUpdateData, setBulkUpdateData] = useState('{}');
  const [organizationId, setOrganizationId] = useState('1');

  const handleTransformAPIs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiAdmin.transformAPIs();
      setTransformResult(response);
      toast.success(`Transformed ${response.transformed_apis} APIs successfully`);
    } catch (error) {
      toast.error('Failed to transform APIs');
      console.error('Transform error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleValidateAPIs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiAdmin.validateAPIs();
      setValidationResult(response);
      toast.success(`Validation complete: ${response.valid_apis} valid, ${response.invalid_apis} invalid`);
    } catch (error) {
      toast.error('Failed to validate APIs');
      console.error('Validation error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleExportAPIs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiAdmin.exportAPIs();
      
      // Create and download file
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `apis_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.length || response.exported || 0} APIs successfully`);
    } catch (error) {
      toast.error('Failed to export APIs');
      console.error('Export error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleImportAPIs = useCallback(async () => {
    if (!selectedFile) {
      toast.error('Please select a file to import');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('organization_id', organizationId);

      const response = await apiAdmin.importAPIs(formData);

      setMigrationResult(response);
      toast.success(`Imported ${response.successful} APIs successfully`);
    } catch (error) {
      toast.error('Failed to import APIs');
      console.error('Import error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, organizationId]);

  const handleBulkUpdate = useCallback(async () => {
    try {
      const updateData = JSON.parse(bulkUpdateData);
      const response = await apiAdmin.bulkUpdateAPIs(updateData);

      setMigrationResult(response);
      toast.success(`Updated ${response.updated} APIs successfully`);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error('Failed to bulk update APIs');
      }
      console.error('Bulk update error:', error);
    }
  }, [bulkUpdateData]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/json') {
      setSelectedFile(file);
    } else {
      toast.error('Please select a valid JSON file');
    }
  }, []);

  const renderTransformTab = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Transform API Configurations</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Automatically fix common API configuration issues like URL patterns, authentication, 
            success codes, and parameter formatting.
          </p>
          <Button 
            onClick={handleTransformAPIs} 
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? 'Transforming...' : 'Transform All APIs'}
          </Button>
        </div>
      </Card>

      {transformResult && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Transformation Results</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{transformResult.total_apis}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total APIs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{transformResult.transformed_apis}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Transformed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{transformResult.errors.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Errors</div>
              </div>
            </div>

            {transformResult.transformations.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium">Changes Made:</h4>
                {transformResult.transformations.map((transformation) => (
                  <div key={transformation.api_id} className="border rounded-lg p-4">
                    <h5 className="font-medium mb-2">{transformation.api_name} (ID: {transformation.api_id})</h5>
                    <div className="space-y-2">
                      {transformation.changes.map((change, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm">
                          <Badge variant="default" className="text-xs">{change.field}</Badge>
                          <span className="text-gray-500 dark:text-gray-400">→</span>
                          <span className="text-gray-700 dark:text-gray-200">{String(change.old_value)}</span>
                          <span className="text-gray-500 dark:text-gray-400">→</span>
                          <span className="text-gray-700 dark:text-gray-200">{String(change.new_value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {transformResult.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">Errors:</h4>
                <div className="space-y-1">
                  {transformResult.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  const renderValidateTab = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Validate API Configurations</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Check all APIs for configuration issues and validation errors.
          </p>
          <Button 
            onClick={handleValidateAPIs} 
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoading ? 'Validating...' : 'Validate All APIs'}
          </Button>
        </div>
      </Card>

      {validationResult && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Validation Results</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{validationResult.total_apis}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total APIs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{validationResult.valid_apis}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Valid</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{validationResult.invalid_apis}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Invalid</div>
              </div>
            </div>

            {validationResult.validation_issues.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-red-600 dark:text-red-400">Validation Issues:</h4>
                {validationResult.validation_issues.map((issue) => (
                  <div key={issue.api_id} className="border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h5 className="font-medium text-red-700 dark:text-red-300 mb-2">{issue.api_name} (ID: {issue.api_id})</h5>
                    <div className="space-y-1">
                      {issue.issues.map((problem, index) => (
                        <div key={index} className="text-sm text-red-600 dark:text-red-400 flex items-center">
                          <span className="mr-2">•</span>
                          {problem}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  const renderExportTab = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Export APIs</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Export all API configurations to a JSON file for backup or migration.
          </p>
          <Button 
            onClick={handleExportAPIs} 
            disabled={isLoading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isLoading ? 'Exporting...' : 'Export All APIs'}
          </Button>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Export Format</h3>
          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
            <pre className="text-sm text-gray-700 dark:text-gray-200 overflow-x-auto">
{`{
  "name": "API Name",
  "description": "API Description",
  "base_url": "https://api.example.com/v1",
  "request_method": "GET",
  "headers": {
    "Accept": "application/json",
    "X-API-Key": "your-api-key"
  },
  "search_parameter_type": "email",
  "organization": "Organization Name",
  "status": "active",
  "price": 5,
  "url_pattern": "{value}",
  "parameter_format": "url_path",
  "parameter_name": "email",
  "country_code_format": "none",
  "auth_type": "api_key",
  "success_status_codes": [200, 201],
  "error_mapping": {
    "400": "Bad request",
    "401": "Unauthorized"
  },
  "rate_limit_per_minute": 30,
  "test_mode_config": {
    "enabled": true,
    "mock_response": {
      "success": true,
      "data": {"found": false}
    }
  }
}`}
            </pre>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderImportTab = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Import APIs</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Import API configurations from a JSON file.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Organization ID
              </label>
              <input
                type="number"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter organization ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                JSON File
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {selectedFile && (
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                  Selected: {selectedFile.name}
                </p>
              )}
            </div>

            <Button 
              onClick={handleImportAPIs} 
              disabled={isLoading || !selectedFile}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isLoading ? 'Importing...' : 'Import APIs'}
            </Button>
          </div>
        </div>
      </Card>

      {migrationResult && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Import Results</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{migrationResult.total_apis}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total APIs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{migrationResult.successful}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{migrationResult.failed}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Failed</div>
              </div>
            </div>

            {migrationResult.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">Errors:</h4>
                <div className="space-y-1">
                  {migrationResult.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  const renderBulkUpdateTab = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Bulk Update APIs</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Update multiple APIs with the same configuration changes.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Update Configuration (JSON)
              </label>
              <textarea
                value={bulkUpdateData}
                onChange={(e) => setBulkUpdateData(e.target.value)}
                className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder='{"rate_limit_per_minute": 60, "retry_count": 2}'
              />
            </div>

            <Button 
              onClick={handleBulkUpdate} 
              disabled={isLoading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isLoading ? 'Updating...' : 'Bulk Update APIs'}
            </Button>
          </div>
        </div>
      </Card>

      {migrationResult && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Bulk Update Results</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{migrationResult.total_apis}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total APIs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{migrationResult.updated}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Updated</div>
              </div>
            </div>

            {migrationResult.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">Errors:</h4>
                <div className="space-y-1">
                  {migrationResult.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">API Content Transformer</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Transform, validate, export, import, and bulk update API configurations
            </p>
          </div>
          <MasterDataImportExport resource="apis" />
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-4 sm:space-x-8 border-b border-gray-200 dark:border-gray-700 overflow-x-auto custom-scrollbar pb-1">
            {[
              { id: 'transform', label: 'Transform', color: 'blue' },
              { id: 'validate', label: 'Validate', color: 'green' },
              { id: 'export', label: 'Export', color: 'purple' },
              { id: 'import', label: 'Import', color: 'orange' },
              { id: 'bulk-update', label: 'Bulk Update', color: 'indigo' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? `border-${tab.color}-500 text-${tab.color}-600`
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'transform' && renderTransformTab()}
          {activeTab === 'validate' && renderValidateTab()}
          {activeTab === 'export' && renderExportTab()}
          {activeTab === 'import' && renderImportTab()}
          {activeTab === 'bulk-update' && renderBulkUpdateTab()}
        </div>
      </div>
    </div>
  );
}; 